<script setup>
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import ProductCard from '@/Components/Public/ProductCard.vue'

// One occasion, told as a day: what we arrange, the boats / villas /
// experiences that fit, a sample timeline, the questions people ask, and
// the plan form prefilled with this occasion.
defineOptions({ layout: PublicLayout })

const { t } = useI18n()
defineProps({
  slug:     { type: String, required: true },
  content:  { type: Object, required: true },   // title, lede, arrange[], sample_day[{time,text}], faq[{q,a}]
  charters: { type: Array,  default: () => [] },
  villas:   { type: Array,  default: () => [] },
  tours:    { type: Array,  default: () => [] },
  siblings: { type: Array,  default: () => [] },
  paths:    { type: Object, required: true }    // charters, villas, tours, occasions, plan
})
</script>

<template>
  <Head>
    <title>{{ content.title }} · {{ t('public.charter_home.head_title') }}</title>
    <meta name="description" :content="content.lede">
  </Head>

  <BrandHero kind="charter" :eyebrow="t('public.occasions.eyebrow')" :title="content.title">
    <template #top>
      <a :href="paths.occasions" class="bhero-back">← {{ t('public.occasions.all') }}</a>
    </template>
    <p class="bhero-lede">{{ content.lede }}</p>
    <div class="bhero-ctas">
      <LuxButton :href="paths.plan" variant="solid" tone="dark" glyph="arrow">{{ t('public.occasions.plan_cta') }}</LuxButton>
      <LuxButton :href="paths.charters" variant="line" tone="dark">{{ t('public.charter_home.hero.cta_fleet') }}</LuxButton>
    </div>
  </BrandHero>

  <section class="body">
    <div class="inner">
      <div class="arrange">
        <div>
          <p class="eyebrow">{{ t('public.occasions.arrange_title') }}</p>
          <ul class="arrange__list">
            <li v-for="(item, i) in content.arrange" :key="i">{{ item }}</li>
          </ul>
        </div>
        <aside class="plan-card">
          <p class="plan-card__text">{{ t('public.occasions.plan_band') }}</p>
          <LuxButton :href="paths.plan" variant="solid" tone="light" glyph="arrow">{{ t('public.occasions.plan_cta') }}</LuxButton>
        </aside>
      </div>

      <section class="block">
        <div class="block__head">
          <h2 class="h2">{{ t('public.occasions.boats_title') }}</h2>
          <a :href="paths.charters" class="more">{{ t('public.charter_home.fleet.all') }} →</a>
        </div>
        <ul v-if="charters.length" class="grid">
          <li v-for="c in charters" :key="c.id"><ProductCard kind="charter" :item="c" :href="`${paths.charters}/${c.id}`" /></li>
        </ul>
        <p v-else class="empty">{{ t('public.occasions.boats_empty') }} <a :href="paths.plan" class="more">{{ t('public.occasions.plan_cta') }} →</a></p>
      </section>

      <section class="block">
        <div class="block__head">
          <h2 class="h2">{{ t('public.occasions.villas_title') }}</h2>
          <a :href="paths.villas" class="more">{{ t('public.charter_home.villas.cta') }} →</a>
        </div>
        <ul v-if="villas.length" class="grid">
          <li v-for="v in villas" :key="v.id"><ProductCard kind="property" :item="v" :href="`${paths.villas}/${v.id}`" /></li>
        </ul>
        <p v-else class="empty">{{ t('public.occasions.villas_empty') }}</p>
      </section>

      <section v-if="tours.length" class="block">
        <div class="block__head">
          <h2 class="h2">{{ t('public.occasions.experiences_title') }}</h2>
          <a :href="paths.tours" class="more">{{ t('public.charter_home.experiences.all') }} →</a>
        </div>
        <ul class="grid">
          <li v-for="x in tours" :key="x.id"><ProductCard kind="tour" :item="x" :href="`${paths.tours}/${x.id}`" /></li>
        </ul>
      </section>

      <div class="two">
        <section class="block block--card">
          <h2 class="h2 h2--small">{{ t('public.occasions.sample_day_title') }}</h2>
          <ol class="day">
            <li v-for="(s, i) in content.sample_day" :key="i"><span class="day__time mono">{{ s.time }}</span><span class="day__text">{{ s.text }}</span></li>
          </ol>
        </section>
        <section class="block block--card">
          <h2 class="h2 h2--small">{{ t('public.occasions.faq_title') }}</h2>
          <details v-for="(f, i) in content.faq" :key="i" class="faq">
            <summary class="faq__q">{{ f.q }}</summary>
            <p class="faq__a">{{ f.a }}</p>
          </details>
        </section>
      </div>

      <nav class="siblings" :aria-label="t('public.occasions.others_title')">
        <span class="siblings__label">{{ t('public.occasions.others_title') }}</span>
        <a v-for="s in siblings" :key="s.slug" :href="`${paths.occasions}/${s.slug}`" class="chip">{{ s.title }}</a>
      </nav>

      <div class="plan-band">
        <p>{{ t('public.occasions.plan_band') }}</p>
        <LuxButton :href="paths.plan" variant="solid" tone="light" glyph="arrow">{{ t('public.occasions.plan_cta') }}</LuxButton>
      </div>
    </div>
  </section>
</template>

<style scoped>
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-lede { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 54ch; opacity: 0.94; }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }

.body { background: var(--bg); }
.inner { max-width: 80rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; display: grid; gap: clamp(2.5rem, 5vw, 4rem); }
.eyebrow { margin: 0 0 0.75rem; font-size: 0.75rem; letter-spacing: var(--eyebrow-tracking); text-transform: uppercase; font-weight: 700; color: var(--accent); }
.h2 { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: clamp(1.8rem, 3.2vw, 2.6rem); line-height: 1.05; margin: 0; color: var(--ink); text-wrap: balance; }
.h2--small { font-size: 1.7rem; margin-bottom: 1rem; }
.more { font-size: 0.9rem; font-weight: 700; color: var(--azure, var(--second)); text-decoration: none; white-space: nowrap; }
.more:hover { text-decoration: underline; }
.mono { font-family: var(--font-mono, var(--mono)); }

.arrange { display: grid; grid-template-columns: 1.4fr 1fr; gap: 2rem; align-items: start; }
.arrange__list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.7rem; }
.arrange__list li { position: relative; padding-left: 1.8rem; font-size: 1.08rem; line-height: 1.5; color: var(--ink); }
.arrange__list li::before { content: "✓"; position: absolute; left: 0; top: 0.05rem; color: var(--ok); font-weight: 700; }
.plan-card { background: linear-gradient(160deg, var(--sand, var(--surfaceAlt)) 0%, var(--accentSoft) 100%); border-radius: 1.25rem; padding: 1.5rem; display: grid; gap: 1rem; justify-items: start; }
.plan-card__text { margin: 0; font-family: var(--font-display, var(--display)); font-size: 1.35rem; line-height: 1.25; color: var(--ink); }

.block__head { display: flex; justify-content: space-between; align-items: end; gap: 1rem; margin-bottom: 1.25rem; }
.grid { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr)); gap: 1.25rem; }
.empty { color: var(--inkSoft); margin: 0; display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: center; }
.block--card { background: var(--surface); border: 1px solid var(--line); border-radius: 1.25rem; padding: 1.5rem; }
.two { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; align-items: start; }
.day { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.75rem; }
.day li { display: grid; grid-template-columns: 4.5rem 1fr; gap: 0.75rem; border-top: 1px solid var(--line); padding-top: 0.75rem; }
.day li:first-child { border-top: 0; padding-top: 0; }
.day__time { font-size: 0.8rem; letter-spacing: 0.04em; color: var(--accent); padding-top: 0.2rem; }
.day__text { color: var(--inkSoft); line-height: 1.5; }
.faq { border-top: 1px solid var(--line); padding: 0.75rem 0; }
.faq:first-of-type { border-top: 0; padding-top: 0; }
.faq__q { cursor: pointer; font-weight: 600; color: var(--ink); }
.faq__a { margin: 0.5rem 0 0; color: var(--inkSoft); line-height: 1.55; }
.siblings { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
.siblings__label { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); font-weight: 700; margin-right: 0.25rem; }
.chip { display: inline-flex; padding: 0.55rem 1rem; border-radius: 999px; background: var(--surface); border: 1px solid var(--line); color: var(--ink); font-size: 0.9rem; font-weight: 600; text-decoration: none; }
.chip:hover { border-color: color-mix(in oklch, var(--gold) 70%, var(--line)); }
.plan-band { padding: 1.5rem 1.75rem; border-radius: 1.25rem; background: linear-gradient(160deg, var(--sand, var(--surfaceAlt)) 0%, var(--accentSoft) 100%); display: flex; flex-wrap: wrap; gap: 1rem; align-items: center; justify-content: space-between; }
.plan-band p { margin: 0; color: var(--ink); font-family: var(--font-display, var(--display)); font-size: 1.35rem; line-height: 1.2; max-width: 40ch; }

@media (max-width: 56rem) {
  .arrange, .two { grid-template-columns: 1fr; }
}
</style>
